import { DateTime } from 'luxon';
import { type Ref, computed } from 'vue';

import type { CreditUsageResponse } from '@/composables/credits/useCreditUsage';
import {
  CREDIT_LIMIT_DANGER_THRESHOLD,
  CREDIT_LIMIT_WARNING_THRESHOLD,
} from '@/constants/billing';
import { computeEffectiveCreditLimit } from '@/utils/creditLimitStatus';

/* The Bitts color token shared by every usage meter (credit and record export).
 * A single status value drives both copy and color everywhere. */
export type UsageStatusColor = 'secondary' | 'warning' | 'danger';

/* Credit limit status maps to the standardized 90 / 100 thresholds shared by
 * the credit usage card, callout, and modal. */
export type CreditLimitStatus = UsageStatusColor;

export default function useCreditLimits(data: Ref<CreditUsageResponse | null>) {
  const creditsUsed = computed(() => data.value?.credits_used ?? null);
  const creditLimit = computed(() =>
    computeEffectiveCreditLimit(
      data.value?.credit_limit,
      data.value?.credit_limit_excess,
    ),
  );

  const limitUsedDecimal = computed(() => {
    if (!creditLimit.value || creditsUsed.value === null) return 0;
    return Math.min(creditsUsed.value, creditLimit.value) / creditLimit.value;
  });

  const percentFormatter = new Intl.NumberFormat(navigator.language, {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });

  const limitUsedPercent = computed(() =>
    percentFormatter.format(limitUsedDecimal.value),
  );

  /* A CSS-safe width for the progress bar. limitUsedPercent is locale-formatted
   * (e.g. "92,3 %" in fr/de) and invalid as a CSS length, so the bar needs a
   * plain ASCII percentage instead. */
  const limitUsedWidth = computed(
    () => `${(limitUsedDecimal.value * 100).toFixed(1)}%`,
  );

  const creditLimitStatus = computed<CreditLimitStatus>(() => {
    if (limitUsedDecimal.value >= CREDIT_LIMIT_DANGER_THRESHOLD)
      return 'danger';
    if (limitUsedDecimal.value >= CREDIT_LIMIT_WARNING_THRESHOLD)
      return 'warning';
    return 'secondary';
  });

  const creditResetDate = computed(() => {
    if (!data.value?.current_term_end) return null;
    const parsed = DateTime.fromISO(data.value.current_term_end, {
      zone: 'utc',
    });
    return parsed.isValid ? parsed.toFormat('MMMM d, yyyy') : null;
  });

  return {
    creditsUsed,
    creditLimit,
    limitUsedDecimal,
    limitUsedPercent,
    limitUsedWidth,
    creditLimitStatus,
    creditResetDate,
  };
}
